import { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, PartyPopper } from 'lucide-react';

interface MovedNoticeModalProps {
    isOpen: boolean;
    onClose: () => void;
}

export function MovedNoticeModal({ isOpen, onClose }: MovedNoticeModalProps) {
    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                onClose();
            }
        };

        if (isOpen) {
            window.addEventListener('keydown', handleEscape);
            return () => window.removeEventListener('keydown', handleEscape);
        }
    }, [isOpen, onClose]);

    return (
        <AnimatePresence>
            {isOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center">
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/50 backdrop-blur-sm"
                        onClick={onClose}
                    />
                    <motion.div
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.8 }}
                        className="relative z-50 w-[95%] max-w-lg rounded-lg bg-white p-6 shadow-xl dark:bg-gray-800"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-2xl font-semibold dark:text-white flex items-center gap-2">
                                <PartyPopper className="text-blue-500" />
                                We moved!
                            </h2>
                            <button
                                onClick={onClose}
                                aria-label="Close"
                                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                            >
                                <X size={24} />
                            </button>
                        </div>

                        <div className="space-y-4 text-gray-600 dark:text-gray-300">
                            <p>
                                Audible Tools now lives at{' '}
                                <span className="font-medium text-gray-900 dark:text-white">
                                    audible-tools.codestars.at
                                </span>
                                . Everything works just like before.
                            </p>

                            <div className="bg-blue-50 dark:bg-blue-900/30 p-4 rounded-lg text-blue-700 dark:text-blue-300">
                                Please update your bookmarks. If you installed the app,
                                reinstall it from this address.
                            </div>

                            <div className="flex justify-end">
                                <button
                                    onClick={onClose}
                                    className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
                                >
                                    Got it
                                </button>
                            </div>
                        </div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );
}
